import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, ChefHat, LogOut, LogIn, User } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';

export const Header = () => {
  const location = useLocation();
  const { count } = useCart();
  const { user, openAuthModal, logout } = useAuth();
  const links = [
    { to: '/', label: 'Home' },
    { to: '/menu', label: 'Menu' },
    { to: '/orders', label: 'My Orders' },
  ];

  return (
    <header className="hidden md:block sticky top-0 z-40 bg-maroon shadow-soft">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2 group">
          <motion.div
            initial={{ rotate: -10 }}
            animate={{ rotate: 0 }}
            whileHover={{ rotate: 10, scale: 1.1 }}
            className="w-10 h-10 rounded-full bg-gradient-primary flex items-center justify-center shadow-warm"
          >
            <img src="/pushpanjaliLogoMain.png" alt="Logo" className="w-full h-full object-contain" />
            {/* <ChefHat className="w-5 h-5 text-white" /> */}
          </motion.div>
          <div>
            <h1 className="text-white font-heading font-bold text-xl leading-none">Pushpanjali</h1>
            <p className="text-golden text-xs font-body">Authentic • Fresh • Homemade</p>
          </div>
        </Link>
        <nav className="flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, '-')}`}
              className={`font-body text-sm font-medium transition-colors ${
                location.pathname === l.to ? 'text-golden' : 'text-white hover:text-golden'
              }`}
            >
              {l.label}
            </Link>
          ))}
          <Link to="/cart" data-testid="header-cart-link" className="relative">
            <ShoppingCart className="w-6 h-6 text-white" />
            {count > 0 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                data-testid="cart-badge-desktop"
                className="absolute -top-2 -right-2 bg-saffron text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
              >
                {count}
              </motion.span>
            )}
          </Link>
          {user ? (
            <div className="flex items-center gap-2">
              <Link to="/profile" data-testid="header-profile-link" className="flex items-center gap-2">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border-2 border-golden" data-testid="header-user-avatar" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-golden text-maroon flex items-center justify-center font-heading font-bold text-sm">
                    {(user.name || user.email)[0].toUpperCase()}
                  </div>
                )}
              </Link>
              <button onClick={logout} data-testid="header-logout-btn" className="text-white/80 hover:text-golden">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button onClick={openAuthModal} data-testid="header-signin-btn" className="flex items-center gap-2 bg-golden hover:bg-golden-light text-maroon font-heading font-semibold px-4 py-2 rounded-2xl shadow-warm transition-colors text-sm">
              <LogIn className="w-4 h-4" /> Sign in
            </button>
          )}
        </nav>
      </div>
    </header>
  );
};

export const MobileTopBar = () => {
  const { count } = useCart();
  const { user, openAuthModal, logout } = useAuth();
  return (
    <div className="md:hidden sticky top-0 z-40 bg-maroon px-4 py-3 flex items-center justify-between shadow-soft">
      <Link to="/" className="flex items-center gap-2" data-testid="mobile-logo">
        <div className="w-9 h-9 rounded-full bg-gradient-primary flex items-center justify-center">
          <img src="/pushpanjaliLogoMain.png" alt="Logo" className="w-full h-full object-contain" />
        </div>
        <div>
          <div className="text-white font-heading font-bold text-lg leading-none">Pushpanjali</div>
          <div className="text-golden text-[10px]">Authentic • Fresh • Homemade</div>
        </div>
      </Link>
      <div className="flex items-center gap-3">
        {user ? (
          <button onClick={() => { window.location.href = '/profile'; }} data-testid="mobile-user-btn" className="flex items-center gap-2">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full border-2 border-golden" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-golden text-maroon flex items-center justify-center font-heading font-bold text-xs">
                {(user.name || user.email)[0].toUpperCase()}
              </div>
            )}
          </button>
        ) : (
          <button onClick={openAuthModal} data-testid="mobile-signin-btn" className="text-golden text-xs font-heading font-semibold">Sign in</button>
        )}
        <Link to="/cart" className="relative" data-testid="mobile-cart-link">
          <ShoppingCart className="w-6 h-6 text-white" />
          {count > 0 && (
            <span data-testid="cart-badge-mobile" className="absolute -top-2 -right-2 bg-saffron text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {count}
            </span>
          )}
        </Link>
      </div>
    </div>
  );
};
